import { uiActions } from './ui-slice';
import { authActions } from './auth-slice';
import { shouldIgnoreUnauthorized } from '../functions/staleUnauthorized';

const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please log in again.';

// Track if we've already shown session expired message to prevent duplicates
let sessionExpiredShown = false;

/**
 * Handles session expiration - shows notification BEFORE logout (so it has time to render)
 * This matches popular website patterns (GitHub, Google, etc.)
 * @param {Function} dispatch - Redux dispatch function
 * @param {string} [requestToken] - JWT that received the 401, if known
 */
export const handleSessionExpired = (dispatch, requestToken) => {
    const tokenUsed =
        typeof requestToken === 'string' && requestToken.indexOf('Bearer ') === 0
            ? requestToken.slice(7)
            : requestToken;
    // Token rotation (support exit / company switch) invalidates in-flight requests.
    if (shouldIgnoreUnauthorized(tokenUsed)) {
        return;
    }

    // Show notification only once (prevents duplicate notifications from multiple simultaneous 401s)
    if (!sessionExpiredShown) {
        sessionExpiredShown = true;
        
        // Show notification FIRST (before logout) so it has time to render
        dispatch(uiActions.showSidePopup({
            status: 'warning',
            header: 'Session Expired',
            message: 'Please log in again',
            autoDismiss: true,
            duration: 2500  // 2.5 seconds - brief like popular sites
        }))
        
        // Reset flag after notification duration
        setTimeout(() => {
            sessionExpiredShown = false;
        }, 2500);
        
        // Delay logout slightly to ensure notification renders before redirect
        // This follows React best practices: allow UI updates to render before state changes that trigger navigation
        setTimeout(() => {
            dispatch(authActions.logout());
        }, 100); // Small delay (100ms) ensures notification renders
    } else {
        // If notification already shown, logout immediately (prevents delay on subsequent 401s)
        dispatch(authActions.logout());
    }
};

/**
 * Handles error display following popular website patterns (GitHub, Google, etc.)
 * - Session expiration: Brief, non-intrusive toast (2-3 seconds), then silent redirect
 * - Other errors: Standard error message modal
 * @param {Function} dispatch - Redux dispatch function
 * @param {Error} error - The error object
 */
export const handleError = (dispatch, error) => {
    if (error.message === SESSION_EXPIRED_MESSAGE) {
        // Session expired errors are handled by handleSessionExpired in action files
        // This catch block is just for safety - shouldn't normally reach here
        // since handleSessionExpired is called before throwing the error
    } else {
        // Other errors use standard error modal
        dispatch(uiActions.showErrorMessage(error.message))
    }
}
